import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Config, 
  Message as MessageType, 
  DailyLimitStatus, 
  HistorySession, 
  Position, 
  DragOffset,
  Size 
} from './types';
import { 
  generateUUID, 
  parseMarkdown, 
  isMobile,
  storage
} from './utils';
import { staticImage, BACKEND_BASE_URL, MAIN_WEBSITE_URL } from './constants';
import Message from './components/Message';
import HistoryView from './components/HistoryView';
import WhatsAppButton from './components/WhatsAppButton';
import WineSelectionButtons from './components/WineSelectionButtons';
import AuthPrompt from './components/AuthPrompt';
import VoiceControls from './components/VoiceControls';
import ShareDialog from './components/ShareDialog';
import { useTranslation } from './hooks/useTranslation';
import { createEventSource } from 'eventsource-client';
import VoiceService from './services/voiceService';

interface ChatWidgetProps {
  initialConfig: Config;
  isVisible: boolean;
  onClose: () => void;
  sessionId?: string;
}

const ChatWidget: React.FC<ChatWidgetProps> = ({
  initialConfig,
  isVisible,
  onClose,
  sessionId,
}) => {
  const [config, setConfig] = useState<Config>(initialConfig);
  const [messages, setMessages] = useState<MessageType[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [isWaitingForResponse, setIsWaitingForResponse] = useState<boolean>(false);
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const [historyData, setHistoryData] = useState<HistorySession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(window.location.search.includes('session_id=') ? new URLSearchParams(window.location.search).get('session_id') : null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragOffset, setDragOffset] = useState<DragOffset>({ x: 0, y: 0 });
  const [position, setPosition] = useState<Position>({ x: 20, y: 20 });
  const [dailyLimitStatus, setDailyLimitStatus] = useState<DailyLimitStatus>({
    remaining: null,
    limit: null,
    isExceeded: false,
    message: ''
  });
  const [displayedOffers, setDisplayedOffers] = useState<Map<string, { oids: string[], messageId: string }>>(new Map());
  const [isResizing, setIsResizing] = useState<boolean>(false);
  const [size, setSize] = useState<Size>({ width: 800, height: window.innerHeight * 0.8 });
  const [resizeStart, setResizeStart] = useState<{ x: number; y: number; width: number; height: number }>({ x: 0, y: 0, width: 0, height: 0 });
  const [selectedCategories, setSelectedCategories] = useState<Record<string, string>>({});
  const [showAuthPrompt, setShowAuthPrompt] = useState<boolean>(false);
  const [userToken, setUserToken] = useState<string | null>(null);
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const [isListening, setIsListening] = useState<boolean>(false);
  const [showShareDialog, setShowShareDialog] = useState<boolean>(false);
  const [hasFirstResult, setHasFirstResult] = useState<boolean>(false);
  const [isSharedConversation, setIsSharedConversation] = useState<boolean>(false);
  const [originalSharedSessionId, setOriginalSharedSessionId] = useState<string | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const widgetRef = useRef<HTMLDivElement>(null);
  const voiceServiceRef = useRef<VoiceService | null>(null);
  const { t } = useTranslation(config.language);

  // Update config when initialConfig changes
  useEffect(() => {
    const prevCurrency = config.currency;
    const prevLanguage = config.language;
    setConfig(initialConfig);
    
    // If currency changed and we have displayed offers, refresh them
    if (prevCurrency !== initialConfig.currency && displayedOffers.size > 0) {
      refreshAllOffers(initialConfig.currency);
    }
    
    // Update voice service language if changed
    if (prevLanguage !== initialConfig.language && voiceServiceRef.current) {
      voiceServiceRef.current.updateLanguage(initialConfig.language || 'en');
    }
  }, [initialConfig, displayedOffers]);

  // Initialize voice service
  useEffect(() => {
    if (!voiceServiceRef.current) {
      voiceServiceRef.current = new VoiceService({
        language: config.language || 'en',
        onTranscript: (text: string) => {
          setInputValue(o => o + ' ' + text);
        },
        onError: (error: Error) => {
          console.error('Voice error:', error);
        },
        onStart: () => setIsListening(true),
        onEnd: () => setIsListening(false)
      });
    }
    
    return () => {
      if (voiceServiceRef.current) {
        voiceServiceRef.current.destroy();
        voiceServiceRef.current = null;
      }
    };
  }, []);

  // Initialize session management and check for token in URL
  useEffect(() => {
    // Check if we're in full-screen mode (widget takes up whole viewport)
    const checkFullScreen = () => {
      const container = document.querySelector('.wwx-chat-widget-container');
      if (container) {
        const rect = container.getBoundingClientRect();
        const isFullViewport = rect.width >= window.innerWidth * 0.95 && rect.height >= window.innerHeight * 0.95;
        setIsFullScreen(isFullViewport);
      }
    };
    
    checkFullScreen();
    window.addEventListener('resize', checkFullScreen);
    
    // Check for token in URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');
    
    if (tokenFromUrl) {
      // Store the token
      setUserToken(tokenFromUrl);
      storage.set('wwx_user_token', tokenFromUrl);
      
      // Update the config with the token
      setConfig(prev => ({ ...prev, user: tokenFromUrl }));
      
      // Clean up the URL to remove the token parameter
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('token');
      window.history.replaceState({}, document.title, newUrl.toString());
      
      console.log('User authenticated via token from URL');
    } else {
      // Check if we have a stored token
      const storedToken = storage.get('wwx_user_token');
      if (storedToken) {
        setUserToken(storedToken);
        setConfig(prev => ({ ...prev, user: storedToken }));
      }
    }
    
    initializeSession();
    
    return () => {
      window.removeEventListener('resize', checkFullScreen);
    };
  }, []);

  // Load history when sessionId prop changes
  useEffect(() => {
    if (sessionId && sessionId !== currentSessionId) {
      loadSessionHistory(sessionId);
      setCurrentSessionId(sessionId);
    }
  }, [sessionId, currentSessionId]);

  // Cleanup EventSource on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  // Drag functionality
  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging || isMobile()) return;
        
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;
        
        const maxX = window.innerWidth - size.width;
        const maxY = window.innerHeight * 0.2;
        
        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        });
      };

      const handleMouseUp = () => {
        setIsDragging(false);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none';
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
      };
    }
  }, [isDragging, dragOffset, size.width]);

  // Resize functionality
  useEffect(() => {
    if (isResizing) {
      const handleMouseMove = (e: MouseEvent) => {
        if (!isResizing || isMobile()) return;
        
        const deltaX = e.clientX - resizeStart.x;
        const deltaY = e.clientY - resizeStart.y;
        
        const newWidth = Math.max(600, Math.min(resizeStart.width + deltaX, window.innerWidth * 0.9));
        const newHeight = Math.max(400, Math.min(resizeStart.height + deltaY, window.innerHeight * 0.9));
        
        setSize({ width: newWidth, height: newHeight });
      };

      const handleMouseUp = () => {
        setIsResizing(false);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'nwse-resize';
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      };
    }
  }, [isResizing, resizeStart]);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  }, []);

  const initializeSession = async (): Promise<void> => {
    const urlParams = new URLSearchParams(window.location.search);
    const requestedSessionId = urlParams.get('session_id');
    const shareId = urlParams.get('share');
    const startNewChat = urlParams.get('new') === 'true';
    const storedSessionId = storage.get('wwx_session_id');

    let sessionToLoad: string | null = null;

    if (shareId) {
      // Handle shared conversation
      console.log(`Loading shared conversation: ${shareId}`);
      await loadSharedConversation(shareId);
      return;
    } else if (startNewChat) {
      console.log("Starting new chat explicitly via 'new=true' parameter.");
      sessionToLoad = generateUUID();
      storage.set('wwx_session_id', sessionToLoad);
      setCurrentSessionId(sessionToLoad);
      setMessages([createWelcomeMessage()]);
    } else if (requestedSessionId) {
      console.log(`Loading session from query param: ${requestedSessionId}`);
      sessionToLoad = requestedSessionId;
      storage.set('wwx_session_id', sessionToLoad);
      setCurrentSessionId(sessionToLoad);
      await loadSessionHistory(sessionToLoad);
    } else if (storedSessionId) {
      console.log(`Continuing session from localStorage: ${storedSessionId}`);
      sessionToLoad = storedSessionId;
      setCurrentSessionId(sessionToLoad);
      await loadSessionHistory(sessionToLoad);
    } else {
      console.log("No session found. Starting new chat implicitly.");
      sessionToLoad = generateUUID();
      storage.set('wwx_session_id', sessionToLoad);
      setCurrentSessionId(sessionToLoad);
      setMessages([createWelcomeMessage()]);
    }
  };

  const loadSharedConversation = async (shareId: string): Promise<void> => {
    try {
      const response = await fetch(`${BACKEND_BASE_URL}/api/shared/${shareId}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        console.error('Shared conversation not found or expired');
        // Start a new session instead
        const newSessionId = generateUUID();
        storage.set('wwx_session_id', newSessionId);
        setCurrentSessionId(newSessionId);
        setMessages([createWelcomeMessage()]);
        return;
      }

      const data = await response.json();
      const { session_id: originalSessionId, history, is_shared } = data;
      
      // Create a new session for this user
      const newSessionId = generateUUID();
      storage.set('wwx_session_id', newSessionId);
      setCurrentSessionId(newSessionId);
      setIsSharedConversation(true);
      setOriginalSharedSessionId(originalSessionId);
      
      // Copy the conversation to the new session
      await fetch(`${BACKEND_BASE_URL}/api/copy_shared`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          original_session_id: originalSessionId,
          new_session_id: newSessionId,
        }),
      });
      
      // Load the history
      if (history && history.length > 0) {
        const loadedMessages = await Promise.all(history.map(async (msg: any) => {
          const message: MessageType = {
            role: msg.role as 'user' | 'assistant',
            content: msg.role === 'assistant' && typeof msg.content === 'string' 
              ? await parseMarkdown(msg.content)
              : msg.content,
            id: generateUUID(),
            offer_ids: msg.offer_ids
          };
          
          if (msg.offer_ids && msg.offer_ids.length > 0) {
            setHasFirstResult(true);
          }
          
          return message;
        }));
        
        setMessages(loadedMessages);
        
        // Clean up the URL to remove the share parameter
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('share');
        window.history.replaceState({}, document.title, newUrl.toString());
      }
    } catch (error) {
      console.error('Error loading shared conversation:', error);
      // Start a new session on error
      const newSessionId = generateUUID();
      storage.set('wwx_session_id', newSessionId);
      setCurrentSessionId(newSessionId);
      setMessages([createWelcomeMessage()]);
    }
  };

  const loadSessionHistory = async (sessionId: string): Promise<void> => {
    try {
      const response = await fetch(`${BACKEND_BASE_URL}/api/history/${sessionId}`, {
        credentials: 'include',
        headers: {
          ...(config.user ? { 'Authorization': `Bearer ${config.user}` } : {})
        }
      });
      if (response.ok) {
        const history = await response.json();
        if (history && history.length > 0) {
          const formattedMessages: MessageType[] = await Promise.all(history.map(async (msg: any) => ({
            role: msg.role,
            content: msg.role === 'assistant' && typeof msg.content === 'string' 
              ? await parseMarkdown(msg.content) 
              : msg.content,
            id: msg.id || generateUUID(),
            offer_ids: msg.offer_ids
          })));
          setMessages(formattedMessages);
        } else {
          setMessages([createWelcomeMessage()]);
        }
      } else {
        setMessages([createWelcomeMessage()]);
      }
    } catch (error) {
      console.error('Error loading session history:', error);
      setMessages([createWelcomeMessage()]);
    }
  };

  const loadHistoryList = async (): Promise<void> => {
    try {
      const response = await fetch(`${BACKEND_BASE_URL}/api/history`, {
        credentials: 'include',
        headers: {
          ...(config.user ? { 'Authorization': `Bearer ${config.user}` } : {})
        }
      });
      if (response.ok) {
        const history = await response.json();
        setHistoryData(history);
        setShowHistory(true);
      }
    } catch (error) {
      console.error('Error loading history list:', error);
    }
  };

  const addMessage = useCallback((content: string | React.ReactNode, role: 'user' | 'assistant', options: Partial<MessageType> = {}): MessageType => {
    const newMessage: MessageType = {
      role,
      content,
      id: generateUUID(),
      ...options
    };
    
    setMessages(prevMessages => [...prevMessages, newMessage]);
    return newMessage;
  }, []);

  const updateLastMessage = useCallback((content: string, removeThinking = false) => {
    setMessages(prevMessages => {
      const newMessages = [...prevMessages];
      
      // Search from the end for the appropriate message
      let targetIndex = -1;
      for (let i = newMessages.length - 1; i >= 0; i--) {
        const message = newMessages[i];
        if (removeThinking) {
          // Look for a message that is not a component and is thinking
          if (!message.isComponent && message.isThinking) {
            targetIndex = i;
            break;
          }
        } else {
          // Look for a message that is not a component
          if (!message.isComponent) {
            targetIndex = i;
            break;
          }
        }
      }
      
      if (targetIndex !== -1) {
        const targetMessage = { ...newMessages[targetIndex] };
        targetMessage.content = content;
        if (removeThinking) {
          targetMessage.isThinking = false;
        }
        newMessages[targetIndex] = targetMessage;
      }
      
      return newMessages;
    });
  }, []);

  const handleFormSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (isWaitingForResponse) {
      console.log("Already waiting for a response. Ignoring new submission.");
      return;
    }

    // Format categories with square brackets and place at end
    const userInput = inputValue.trim();
    const categoryValues = Object.values(selectedCategories)
      .map(value => `[${value}]`)
      .join(' ');
    
    const fullMessage = userInput && categoryValues
      ? `${userInput} ${categoryValues}`
      : userInput || categoryValues;
    
    if (fullMessage) {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      addMessage(fullMessage, 'user');
      const currentThinkingMessage = addMessage(t('thinking'), 'assistant', { isThinking: true });
      setInputValue('');
      setSelectedCategories({}); // Clear selected categories after sending
      setIsWaitingForResponse(true);

      const queryParams = new URLSearchParams({ 
        message: fullMessage,
        session_id: currentSessionId || '',
        currency: config.currency
      });
      
      const es = eventSourceRef.current = createEventSource({
        url: `${BACKEND_BASE_URL}/chat?${queryParams.toString()}`,
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${config.user}`,
        },
        onDisconnect: () => {
          console.log('here');
          es.close();
        },
        onMessage: async ({ data, event, id }) => {
          console.log('Received SSE event:', { event, id, data });
          
          if (!event || event === 'message') {
            // Default message event
            const htmlContent = await parseMarkdown(data.replace(/\\n/g, '\n'));
            updateLastMessage(htmlContent, true);
          } else if (event === 'step') {
            updateLastMessage(t('thinkingStep', { step: data }));
          } else if (event === 'done') {            
            console.log("SSE stream ended by server.");
            es.close();
            setIsWaitingForResponse(false);
            
          } else if (event === 'ui_action') {
            if (data === 'show_whatsapp') {
              addWhatsAppButton();
            }
          } else if (event === 'offers') {
            console.log("Received offers event:", data);
            try {
              const offerIds = JSON.parse(data);
              if (Array.isArray(offerIds) && offerIds.length > 0) {
                // Mark that we have received our first result
                setHasFirstResult(true);
                const targetMessageId = currentThinkingMessage?.id || null;
                fetchOfferDetailsAndDisplayCarousel(offerIds, targetMessageId);
              }
            } catch (e) {
              console.error("Error parsing offer IDs:", e);
            }
          } else if (event === 'limit_status') {
            try {
              const statusData = JSON.parse(data);
              console.log("Received limit_status event:", statusData);
              setDailyLimitStatus({
                remaining: statusData.remaining,
                limit: statusData.limit,
                isExceeded: statusData.is_exceeded,
                message: statusData.message || (statusData.is_exceeded ? t('dailyLimitReached') : '')
              });
              
              if (statusData.is_exceeded) {
                setIsWaitingForResponse(false);
                setMessages(prevMessages => {
                  const lastMsg = prevMessages[prevMessages.length - 1];
                  if (lastMsg && lastMsg.isThinking) {
                    const newMessages = [...prevMessages];
                    const updatedLastMessage = { 
                      ...lastMsg, 
                      content: statusData.message || t('dailyLimitReached'), 
                      isThinking: false 
                    };
                    newMessages[newMessages.length - 1] = updatedLastMessage;
                    return newMessages;
                  }
                  return prevMessages;
                });
                
                // Show auth prompt if user is not logged in
                if (!userToken) {
                  setShowAuthPrompt(true);
                }
              }
            } catch (e) {
              console.error("Error parsing limit_status event:", e);
            }
          }
        }
      });
    }
  }, [isWaitingForResponse, inputValue, currentSessionId, config.currency, messages, addMessage, updateLastMessage, t]);

  const addWhatsAppButton = useCallback(() => {
    const hasWhatsApp = messages.some(msg => 
      msg.isWhatsApp || (typeof msg.content === 'string' && msg.content.includes('whatsapp-button'))
    );
    
    if (!hasWhatsApp) {
      addMessage(<WhatsAppButton language={config.language} />, 'assistant', { 
        isComponent: true,
        isWhatsApp: true 
      });
    }
  }, [messages, addMessage, config.language]);

  const refreshAllOffers = useCallback((currency: string) => {
    // Remove existing carousel messages
    setMessages(prevMessages => 
      prevMessages.filter(msg => !msg.isCarousel)
    );
    
    // Re-fetch each carousel with its original position
    displayedOffers.forEach((offerData) => {
      fetchOfferDetailsAndDisplayCarousel(offerData.oids, offerData.messageId, currency);
    });

    setDisplayedOffers(new Map());
  }, [displayedOffers]);

  const displayOfferCarousel = useCallback((offersData: any[], targetMessageId: string | null = null, oids: string[] = []) => {
    const carouselId = generateUUID();
    
    // Track this carousel with its oids and position
    if (oids.length > 0 && targetMessageId) {
      setDisplayedOffers(prev => {
        const newMap = new Map(prev);
        newMap.set(carouselId, { oids, messageId: targetMessageId });
        return newMap;
      });
    }
    
    const carouselMessage: MessageType = {
      role: 'assistant',
      content: '',
      id: carouselId,
      isCarousel: true,
      carouselData: offersData,
      skipHistoricalProcessing: true,
      targetMessageId: targetMessageId || undefined
    };
    
    if (targetMessageId) {
      setMessages(prevMessages => {
        const targetIndex = prevMessages.findIndex(msg => msg.id === targetMessageId);
        if (targetIndex !== -1) {
          const newMessages = [...prevMessages];
          newMessages.splice(targetIndex, 0, carouselMessage);
          return newMessages;
        }
        return [...prevMessages, carouselMessage];
      });
    } else {
      setMessages(prevMessages => [...prevMessages, carouselMessage]);
    }
  }, []);

  const fetchOfferDetailsAndDisplayCarousel = useCallback((offerIds: string[], targetMessageId: string | null = null, overrideCurrency?: string) => {
    const loadingMessageId = generateUUID();
    const loadingMessage: MessageType = {
      role: 'assistant',
      content: '',
      id: loadingMessageId,
      isLoading: true,
      skipHistoricalProcessing: true,
      targetMessageId: targetMessageId || undefined
    };

    if (targetMessageId) {
      setMessages(prevMessages => {
        const targetIndex = prevMessages.findIndex(msg => msg.id === targetMessageId);
        if (targetIndex !== -1) {
          const newMessages = [...prevMessages];
          newMessages.splice(targetIndex, 0, loadingMessage);
          return newMessages;
        }
        return [...prevMessages, loadingMessage];
      });
    } else {
      setMessages(prevMessages => [...prevMessages, loadingMessage]);
    }

    fetch(`${BACKEND_BASE_URL}/get_offer_details`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...(config.user ? { 'Authorization': `Bearer ${config.user}` } : {}),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        offer_ids: offerIds,
        currency: overrideCurrency || config.currency
      }),
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(offersData => {
      setMessages(prevMessages => prevMessages.filter(msg => msg.id !== loadingMessageId));
      
      if (Array.isArray(offersData) && offersData.length > 0) {
        // Extract oids from the offers data
        const oids = offersData.map((offer: any) => offer.offer_oid).filter(Boolean);
        
        displayOfferCarousel(offersData, targetMessageId, oids);
      }
    })
    .catch(error => {
      console.error('Error fetching offer details:', error);
      setMessages(prevMessages => prevMessages.filter(msg => msg.id !== loadingMessageId));
      
      const errorMessage: MessageType = {
        role: 'assistant',
        content: t('errorLoadingWine'),
        id: generateUUID(),
        skipHistoricalProcessing: true,
        targetMessageId: targetMessageId || undefined
      };
      
      if (targetMessageId) {
        setMessages(prevMessages => {
          const targetIndex = prevMessages.findIndex(msg => msg.id === targetMessageId);
          if (targetIndex !== -1) {
            const newMessages = [...prevMessages];
            newMessages.splice(targetIndex, 0, errorMessage);
            return newMessages;
          }
          return [...prevMessages, errorMessage];
        });
      } else {
        setMessages(prevMessages => [...prevMessages, errorMessage]);
      }
    });
  }, [config.currency, t]);

  const handleHistoricalOffers = useCallback(() => {
    messages.forEach((msg) => {
      if (msg.role === 'assistant' && 
          msg.offer_ids && 
          Array.isArray(msg.offer_ids) && 
          msg.offer_ids.length > 0 && 
          !msg.offersProcessed && 
          !msg.skipHistoricalProcessing) {
        console.log(`Rendering historical offers for message ${msg.id}:`, msg.offer_ids);
        msg.offersProcessed = true;
        fetchOfferDetailsAndDisplayCarousel(msg.offer_ids, msg.id);
      }
    });
  }, [messages, fetchOfferDetailsAndDisplayCarousel]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
    handleHistoricalOffers();
  }, [messages]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (isMobile()) return;
    if (!(e.target as HTMLElement).closest('.xr-banner')) return;
    if ((e.target as HTMLElement).closest('button')) return;
    
    setIsDragging(true);
    const rect = widgetRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
    e.preventDefault();
  }, []);

  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    if (isMobile()) return;
    e.preventDefault();
    e.stopPropagation();
    
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height
    });
  }, [size]);

  const handleNewChat = useCallback(() => {
    const newSessionId = generateUUID();
    storage.set('wwx_session_id', newSessionId);
    setCurrentSessionId(newSessionId);
    setMessages([createWelcomeMessage()]);
    // Clear tracked offers for new chat
    setDisplayedOffers(new Map());
    // Clear selected categories
    setSelectedCategories({});
  }, [config.language, isWaitingForResponse]);

  const handleSelectSession = useCallback((sessionId: string) => {
    loadSessionHistory(sessionId);
    setShowHistory(false);
    // Clear tracked offers when switching sessions
    setDisplayedOffers(new Map());
    // Clear selected categories
    setSelectedCategories({});
  }, []);

  const createWelcomeMessage = useCallback((): MessageType => {
    return {
      role: 'assistant',
      content: '',
      id: generateUUID(),
      isWelcome: true
    };
  }, []);

  const handleSelectionClick = useCallback((category: string, value: string) => {
    if (!isWaitingForResponse) {
      // Update selected categories
      setSelectedCategories((prev: Record<string, string>) => ({
        ...prev,
        [category]: value
      }));
      
      // Don't modify the input value directly since we're showing categories as tags
      // The user can still type additional text in the input
      
      // Focus back on the input
      setTimeout(() => {
        const input = document.getElementById('messageInput') as HTMLInputElement;
        if (input) {
          input.focus();
        }
      }, 50);
    }
  }, [isWaitingForResponse, selectedCategories]);

  const handleSignupClick = useCallback(() => {
    // Redirect to main website signup with sent_from parameter
    window.location.href = `${MAIN_WEBSITE_URL}/signup?sent_from=zyra`;
  }, []);
  
  const handleLoginClick = useCallback(() => {
    // Redirect to main website login with sent_from parameter
    window.location.href = `${MAIN_WEBSITE_URL}/login?sent_from=zyra`;
  }, []);
  
  const handleLogout = useCallback(() => {
    // Clear the stored token
    storage.remove('wwx_user_token');
    setUserToken(null);
    setConfig(prev => ({ ...prev, user: null }));
    
    // Reset session
    const newSessionId = generateUUID();
    storage.set('wwx_session_id', newSessionId);
    setCurrentSessionId(newSessionId);
    setMessages([createWelcomeMessage()]);
    
    console.log('User logged out');
  }, []);

  // Voice control handlers
  const handleStartListening = useCallback(async () => {
    if (voiceServiceRef.current && !isListening) {
      try {
        await voiceServiceRef.current.startListening();
      } catch (error) {
        console.error('Failed to start listening:', error);
      }
    }
  }, [isListening]);

  const handleStopListening = useCallback(() => {
    if (voiceServiceRef.current) {
      voiceServiceRef.current.stopListening();
    }
  }, []);



  // Add method to refresh limit status
  const refreshLimitStatus = useCallback(() => {
    // Reset the limit status to allow new requests
    setDailyLimitStatus({
      remaining: null,
      limit: null,
      isExceeded: false,
      message: ''
    });
  }, []);

  // Expose refresh method to window for testing
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).refreshLimitStatus = refreshLimitStatus;
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete (window as any).refreshLimitStatus;
      }
    };
  }, [refreshLimitStatus]);

  if (!isVisible) {
    return null;
  }

  const containerStyle: React.CSSProperties = {
    position: 'fixed',
    top: isMobile() ? '60px' : `${position.y}px`,
    left: isMobile() ? '10px' : `${position.x}px`,
    right: isMobile() ? '10px' : 'auto',
    bottom: isMobile() ? '20px' : 'auto',
    width: isMobile() ? 'auto' : `${size.width}px`,
    height: isMobile() ? 'auto' : `${size.height}px`,
    maxWidth: isMobile() ? 'none' : '90vw',
    maxHeight: isMobile() ? 'none' : '90vh',
    border: '1px solid #ccc',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    backgroundColor: '#f4f4f4',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1000,
    borderRadius: '10px',
    overflow: 'hidden',
    cursor: isDragging ? 'grabbing' : 'default'
  };

  if (showHistory) {
    return (
      <div 
        ref={widgetRef}
        className="wwx-chat-widget-container" 
        style={containerStyle}
        onMouseDown={handleMouseDown}
      >
        <header className="app-header" style={{ cursor: !isMobile() ? 'grab' : 'default' }}>
          <div className="header-icons" style={{ width: '100%', justifyContent: 'space-between' }}>
            <span style={{ color: '#999', fontWeight: 'bold' }}>{t('chatHistory')}</span>
            <button 
              onClick={onClose} 
              className="close-banner-button" 
              style={{ position: 'static', opacity: 1, marginRight: '10px' }}
            >
              &times;
            </button>
          </div>
        </header>

        <HistoryView 
          historyData={historyData}
          language={config.language}
          onSelectSession={handleSelectSession}
          onBack={() => setShowHistory(false)}
        />

        <div style={{ backgroundColor: '#fff', borderTop: '1px solid #eee', padding: '15px', textAlign: 'center' }}>
          <a 
            href="#" 
            onClick={(e) => { 
              e.preventDefault(); 
              setShowHistory(false); 
            }}
            style={{ color: '#AB7C40', textDecoration: 'none', fontWeight: 'bold' }}
          >
            <i className="fas fa-arrow-left"></i> {t('backToChat')}
          </a>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      ref={widgetRef}
      className="wwx-chat-widget-container" 
      style={containerStyle}
      onMouseDown={handleMouseDown}
    >
      <div className="chatbox-background" style={{ height: '100%', overflow: 'hidden' }}>
        <div className="xr-banner" onMouseDown={handleMouseDown} style={{ cursor: !isMobile() ? 'grab' : 'default' }}>
          <img src={staticImage('xr-avatar.webp')} alt="XR Avatar" className="xr-avatar" />
          <div className="xr-text">
            <span className="xr-name">
              <img src={staticImage('zyra.png')} className="zyra" /> <img src={staticImage('xr-ai.png')} alt="XR Star" className="xr-star" />
            </span>
            <span className="xr-title">{t('wineAdvisor')}</span>
          </div>
          <div className="header-auth-buttons">
            {!userToken && isFullScreen && (
              <>
                <button className="header-auth-btn" onClick={handleSignupClick}>
                  {t('auth.signUp')}
                </button>
                <button className="header-auth-btn header-auth-btn-login" onClick={handleLoginClick}>
                  {t('auth.login')}
                </button>
              </>
            )}
            {userToken && isFullScreen && (
              <button className="header-auth-btn header-auth-btn-logout" onClick={handleLogout}>
                {t('auth.logout')}
              </button>
            )}
          </div>
          <button 
            onClick={onClose} 
            className="close-banner-button"
          >
            &times;
          </button>
        </div>

        <div 
          id="chatbox" 
          style={{ 
            borderRadius: '30px', 
            margin: '10px', 
            height: 'calc(100% - 120px)', 
            paddingBottom: '10px', 
            overflow: 'hidden' 
          }}
        >
          <div 
            id="messages" 
            style={{ 
              height: '100%', 
              overflowY: 'auto', 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '15px', 
            }}
          >
            {messages.map((msg) => (
              <Message 
                key={msg.id} 
                message={msg} 
                currency={config.currency}
                language={config.language}
                user={config.user}
                sessionId={currentSessionId || undefined}
                onSignupClick={handleSignupClick}
              />
            ))}
            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>

      <div className="bottom-bar">
        <nav className="bottom-nav-icons">
          <a 
            href="https://api.whatsapp.com/send?phone=85297100398" 
            target="_blank" 
            className="nav-icon"
            rel="noopener noreferrer"
          >
            <i className="fab fa-whatsapp"></i>
            <span>{t('whatsapp')}</span>
          </a>
          <a href="#" className="nav-icon">
            <i className="fas fa-search"></i>
            <span>{t('search')}</span>
          </a>
          <a 
            href="#" 
            className="nav-icon" 
            onClick={(e) => { 
              e.preventDefault(); 
              handleNewChat();
            }}
          >
            <img src={staticImage('robot.svg')} alt="New Chat Icon" className="new-chat-icon" />
            <span>{t('newChat')}</span>
          </a>
          <a 
            href="#" 
            className="nav-icon" 
            onClick={(e) => { 
              e.preventDefault(); 
              loadHistoryList(); 
            }}
          >
            <i className="fas fa-history"></i>
            <span>{t('history')}</span>
          </a>
          {hasFirstResult && currentSessionId && (
            <a 
              href="#" 
              className="nav-icon" 
              onClick={(e) => { 
                e.preventDefault(); 
                setShowShareDialog(true); 
              }}
            >
              <i className="fas fa-share-alt"></i>
              <span>{t('share.button') || 'Share'}</span>
            </a>
          )}
        </nav>
        
        <div className="message-input-area">
          {showAuthPrompt && (
            <AuthPrompt
              onSignUp={handleSignupClick}
              onLogin={handleLoginClick}
              remainingMessages={dailyLimitStatus.remaining}
              language={config.language}
            />
          )}
          {!showAuthPrompt && dailyLimitStatus.limit !== null && (
            <div className="limit-indicator">
              {dailyLimitStatus.isExceeded 
                ? (dailyLimitStatus.message || t('dailyLimitReached'))
                : t('dailyLimitMessage', { 
                    remaining: dailyLimitStatus.remaining, 
                    limit: dailyLimitStatus.limit 
                  })
              }
            </div>
          )}
          <form id="messageForm" onSubmit={handleFormSubmit}>
            <div className="messageInput">
              {Object.keys(selectedCategories).length > 0 && (
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '6px',
                  marginBottom: '8px'
                }}>
                  {Object.entries(selectedCategories).map(([category, value]) => (
                    <span
                      key={category}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        backgroundColor: '#B8860B',
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      {value}
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedCategories((prev: Record<string, string>) => {
                            const newCategories = { ...prev };
                            delete newCategories[category];
                            return newCategories;
                          });
                          setInputValue((prev: string) => {
                            // Remove this value from input
                            return prev.replace(value as string, '').replace(/\s+/g, ' ').trim();
                          });
                        }}
                        style={{
                          marginLeft: '6px',
                          background: 'none',
                          border: 'none',
                          color: 'white',
                          cursor: 'pointer',
                          padding: 0,
                          fontSize: '14px',
                          lineHeight: 1
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <input
                type="text"
                id="messageInput"
                placeholder={
                  dailyLimitStatus.isExceeded 
                    ? (dailyLimitStatus.message || t('dailyLimitReached')) 
                    : (isWaitingForResponse ? t('waitingForResponse') : (messages.length <= 1 ? t('askAboutWineFirst') : t('askAboutWine')))
                }
                autoComplete="off"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                disabled={isWaitingForResponse || dailyLimitStatus.isExceeded}
              />
              <WineSelectionButtons 
                onSelectionClick={handleSelectionClick}
                language={config.language || 'en'}
              />
            </div>
            <div className="input-buttons-stack">
              <VoiceControls
                isListening={isListening}
                onStartListening={handleStartListening}
                onStopListening={handleStopListening}
                disabled={isWaitingForResponse || dailyLimitStatus.isExceeded}
                language={config.language || 'en'}
              />
              <button type="submit" className="send-button" disabled={isWaitingForResponse || dailyLimitStatus.isExceeded}>
                <i className="fas fa-paper-plane"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {!isMobile() && (
        <div
          className="resize-handle"
          onMouseDown={handleResizeMouseDown}
          style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: '20px',
            height: '20px',
            cursor: 'nwse-resize',
            background: 'linear-gradient(135deg, transparent 50%, #AB7C40 50%)',
            borderBottomRightRadius: '10px'
          }}
        />
      )}
      
      {showShareDialog && currentSessionId && (
        <ShareDialog
          sessionId={currentSessionId}
          isOpen={showShareDialog}
          onClose={() => setShowShareDialog(false)}
          language={config.language}
        />
      )}
    </div>
  );
};

export default ChatWidget;
